import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Phone, Mail } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Footer = () => {
  const [logoHover, setLogoHover] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId, isPage = false) => {
    if (isPage) {
      navigate(`/${sectionId}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // If we're on a different page, navigate home first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 90;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const aboutLinks = [
    { name: 'About Us', id: 'about', isPage: true },
    { name: 'FAQs', id: 'faq', isPage: true }
  ];

  const serviceLinks = [
    { name: 'Injectables', id: 'injectables', isPage: true },
    { name: 'Facials', id: 'facials', isPage: true },
    { name: 'Body Treatments', id: 'body-treatments', isPage: true },
    { name: 'HydraFacial', id: 'hydrafacial', isPage: true },
    { name: 'IV Therapy', id: 'iv-therapy', isPage: true },
    { name: 'Men\'s Health', id: 'mens-health', isPage: true },
    { name: 'Peptides', id: 'peptides', isPage: true },
    { name: 'Muscle Sculpting', id: 'muscle-sculpting', isPage: true },
    { name: 'Massage', id: 'massage', isPage: true },
    { name: 'Laser Hair Removal', id: 'laser-hair-removal', isPage: true },
    { name: 'Waxing', id: 'waxing', isPage: true },
    { name: 'Medical Weight Loss', id: 'medical-weight', isPage: true },
    { name: 'Pigmented Lesion', id: 'pigmented-lesion', isPage: true },
    { name: 'Vascular Lesion', id: 'vascular-lesion', isPage: true },
    { name: 'Skin Tags', id: 'skin-tag', isPage: true }
  ];

  const membershipLinks = [
    { name: 'Exclusive Memberships', id: 'exclusive-memberships', isPage: true },
    { name: 'Men\'s Memberships', id: 'mens-memberships', isPage: true }
  ];

  const reviewLinks = [
    { name: 'Testimonials', id: 'testimonials' },
    { name: 'Google Reviews', id: 'google-reviews', isPage: true }
  ];

  const quickLinks = [
    { name: 'Home', id: 'hero' },
    { name: 'Contact', id: 'contact' },
  ];

  return (
    <footer className="bg-olive-darkest text-white">
      <div className="max-w-7xl mx-auto px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-7 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div 
              className="flex items-center gap-4 mb-6 cursor-pointer"
              onClick={() => scrollToSection('hero')}
              onMouseEnter={() => setLogoHover(true)}
              onMouseLeave={() => setLogoHover(false)}
            >
              <img 
                src="/longevity.png" 
                alt="Century Medical Logo" 
                className="h-20 w-auto transition-all duration-300"
                style={{ 
                  filter: logoHover 
                    ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
                    : 'brightness(0) saturate(100%) invert(65%) sepia(35%) saturate(500%) hue-rotate(5deg) brightness(95%) contrast(90%)'
                }}
              />
            </div>
            <p className="text-white/80 leading-relaxed mb-6 max-w-md">
              Century Longevity & Aesthetics is situated in the heart of Forest Hills, NY, specializing in Medical and Aesthetic treatments.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href="tel:3325294781" 
                className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                <span>(332) 529-4781</span>
              </a>
              <a 
                href="mailto:centuryaesthetics@gmail.com" 
                className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors duration-300"
              >
                <Mail className="w-4 h-4" />
                <span>centuryaesthetics@gmail.com</span>
              </a>
            </div>

            {/* Social Media */}
            <div className="flex gap-4 mt-6">
              <motion.a
                href="https://www.instagram.com/century.aesthetics/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -3 }}
                className="bg-white/10 p-3 rounded-full hover:bg-gold transition-all duration-300 group"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white group-hover:text-olive-darkest transition-colors duration-300" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-gold mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id, link.isPage)}
                    className="text-white/80 hover:text-gold transition-colors duration-300 hover:pl-2 text-left w-full text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* About Us */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-gold mb-6">
              About Us
            </h4>
            <ul className="space-y-3">
              {aboutLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id, link.isPage)}
                    className="text-white/80 hover:text-gold transition-colors duration-300 hover:pl-2 text-left w-full text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-gold mb-6">
              Services
            </h4>
            <ul className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
              {serviceLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id, link.isPage)}
                    className="text-white/80 hover:text-gold transition-colors duration-300 hover:pl-2 text-left w-full text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Memberships */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-gold mb-6">
              Memberships
            </h4>
            <ul className="space-y-3">
              {membershipLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id, link.isPage)}
                    className="text-white/80 hover:text-gold transition-colors duration-300 hover:pl-2 text-left w-full text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Reviews */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-gold mb-6">
              Reviews
            </h4>
            <ul className="space-y-3">
              {reviewLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id, link.isPage)}
                    className="text-white/80 hover:text-gold transition-colors duration-300 hover:pl-2 text-left w-full text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Office Hours */}
        <div className="border-t border-white/10 pt-8 mb-8 mt-8">
          <div className="grid md:grid-cols-3 gap-6 text-center md:text-left">
            <div>
              <h5 className="font-semibold text-gold mb-2">Office Hours</h5>
              <p className="text-white/80 text-sm">
                Monday: 9:00 AM - 5:00 PM<br />
                Tuesday: 12:00 PM - 7:00 PM<br />
                Wednesday: 8:00 AM - 4:00 PM<br />
                Thursday: 8:00 AM - 4:00 PM<br />
                Friday: 8:00 AM - 2:00 PM<br />
                Saturday: 1st Saturday of Each Month, 9:00 AM - 2:00 PM
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gold mb-2">Location</h5>
              <a 
                href="https://www.google.com/maps/place/Century+-+Longevity+%26+Aesthetics/@40.722696,-73.8571024,854m/data=!3m2!1e3!4b1!4m6!3m5!1s0x89c25f3efd11ec71:0x59b5c67e4f9c82e!8m2!3d40.722692!4d-73.8522315!16s%2Fg%2F11msbj1tpy!5m1!1e2?entry=ttu&g_ep=EgoyMDI2MDEyMS4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 text-sm hover:text-gold transition-colors duration-300"
              >
                70-64 Yellowstone Boulevard Unit <br />
                D2 Forest Hills, NY 11375
              </a>
            </div>
            <div>
              <h5 className="font-semibold text-gold mb-2">Emergency</h5>
              <p className="text-white/80 text-sm">
                Call <a href="tel:911" className="font-semibold text-gold hover:text-white transition-colors duration-300">911</a> or email{' '}
                <a 
                  href="mailto:centuryaesthetics@gmail.com" 
                  className="font-semibold text-gold hover:text-white transition-colors duration-300"
                >
                  centuryaesthetics@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
          <p>&copy; 2026 Century Longevity & Aesthetics. All rights reserved.</p>
          <div className="flex gap-6">
            <button
              onClick={() => scrollToSection('privacy-policy', true)}
              className="hover:text-gold transition-colors duration-300"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;